import {
  CellClickedEvent,
  ColDef,
  FilterModel,
  GridApi,
  GridOptions,
} from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import logo from '@assets/logo.svg';
import { Box, Stack } from '@atgfr8/atg-ui-tailwind';
import { AtgLoader } from '@components/AtgLoader';
import { FeatureFlags } from '@constants/featureFlags';
import { getAllPendingApprovals } from '@store/Global/actions';
import {
  deserializeWsDataJson,
  deserializeWsMessage,
} from '@store/Global/socketMessage';
import { PendingApprovals, WSEventTypeEnum } from '@store/Global/types';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  ILoadBoardViewModel,
  LoadBoardMetaDataT,
  addLoadboardItem,
  autoHeightColumns,
  calculateNewHeight,
  loadBoardStore,
  removeLoadboardItem,
  updateLoadboardItem,
} from '@store/loadboard';
import { RelativeDateInput } from '@store/loadboard/RelativeDateInput';
import {
  currencyCellFormatter,
  underlinedCellRenderer,
} from '@store/loadboard/utils';
import { userStore } from '@store/user';
import { ConfigManager } from '@utils/ConfigManager';
import { routeOrderDetailsNewTab } from '@utils/routeOrderDetailsNewTab';
import { printLog } from '@utils/utils';
import OverdueApprovalsWarning from '@views/loadboard/OverdueApprovalsWarning';
import { TransitWarning } from '@views/loadboard/TransitWarning';
import { memoize } from 'lodash';
import {
  Accessor,
  Component,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from 'solid-js';

import { FlagType, globalState, sidebarDataStore } from '../../store';
import '../Grid/AgGridStyles.css';
import Filters from './components/Filters';
import Pagination from './components/Pagination';
import { columnsToolPanel, filtersToolPanel } from './components/constants';
import { getRowStyle } from './utils';

export type GridProps = {
  gridReady: boolean;
  data: Record<string, unknown>[];
  oldPicks: ILoadBoardViewModel[];
  oldDrops: ILoadBoardViewModel[];
  loading: boolean;
  onUpdateItems: (newItem: string, col: string, loadId: number) => void;
  onUpdatePostings: (postings: ILoadBoardViewModel[]) => void;
  columnDefs: Array<ColDef>;
  columns?: string[];
  singlePage?: boolean;
  allowFiltering?: boolean;
  onRowClicked?: (e: CellClickedEvent, col?: string) => void;
  onRefreshGrid?: () => void;
  onRefreshLoadboardRow?: (orderId: number) => Promise<void>;
  isRefreshingLoadboardRow?: Accessor<boolean>;
  onGridReady?: (gridApi: GridApi) => void;
  extraOptions?: GridOptions;
  customFilterCache: Accessor<string>;
  setCustomFilterCache: (filter: string) => void;
  updatePicksAndDrops?: () => void;
  showOptimized?: boolean;
  setRestrictToMyUserId?: (n: number) => void;
};

const imageTag = `<img src=${logo} alt="" />`;

export const Grid: Component<GridProps> = (props: GridProps) => {
  let gridRef: AgGridSolidRef | undefined;
  const [socketHandlerInitialized, setSocketHandlerInitialized] =
    createSignal(false);
  const [pagelimit, setPageLimit] = createSignal(10);
  const [pageNumber, setPageNumber] = createSignal(1);
  const [total, setTotal] = createSignal(0);
  const [showingCount, setShowingCount] = createSignal({
    start: 1,
    end: pagelimit(),
  });
  const [gridApi, setGridApi] = createSignal<GridApi | undefined>();
  const [filterModel, setFilterModel] = createSignal<FilterModel>({});
  const [overdueApprovalsData, setOverdueApprovalsData] = createSignal<
    PendingApprovals[]
  >([]);

  const showLoading = () => {
    gridRef?.api.showLoadingOverlay();
  };

  const hideLoading = () => {
    gridRef?.api.hideOverlay();
  };

  onMount(async () => {
    if (ConfigManager.showLayout) {
      await getAllPendingApprovals();
    }
  });

  createEffect(() => {
    if (loadBoardStore.overlay === true) {
      showLoading();
    }
    if (
      loadBoardStore.overlay === false &&
      (gridRef?.api as unknown) !== undefined
    ) {
      hideLoading();
      gridRef?.api.redrawRows();
    }
  });

  onCleanup(function () {
    void (async () => {
      if (globalState.socketv !== undefined) {
        await globalState.socketv.invoke(
          'Unsubscribe',
          WSEventTypeEnum.Updated,
        );
        await globalState.socketv.invoke(
          'Unsubscribe',
          WSEventTypeEnum.Deleted,
        );
        await globalState.socketv.invoke(
          'Unsubscribe',
          WSEventTypeEnum.Created,
        );
        globalState.socketv.off('RecieveWebSocketMessage', socketHandler);
      }
    })();
  });

  // eslint-disable-next-line complexity
  const socketHandler = (message: unknown) => {
    const msgObj = deserializeWsMessage(message);
    if (msgObj == null) {
      return;
    }

    const loadMetaModel = deserializeWsDataJson<LoadBoardMetaDataT>(
      msgObj.dataJson,
    );
    if (loadMetaModel == null) {
      return;
    }

    const load = loadMetaModel.loadBoardLine;

    if (!!load) {
      load.pickDate = new Date(new Date(load.pickDate).toDateString());
      load.dropDate = new Date(new Date(load.dropDate).toDateString());

      if (
        load.lastCheckCallDate !== null &&
        load.lastCheckCallDate !== undefined
      ) {
        load.lastCheckCallDate = new Date(
          load.lastCheckCallDate.toString().includes('Z')
            ? load.lastCheckCallDate.toString()
            : load.lastCheckCallDate.toString() + 'Z',
        );
      }

      if (
        load.nextCheckCallDateUtc !== null &&
        load.nextCheckCallDateUtc !== undefined
      ) {
        load.nextCheckCallDateUtc = new Date(
          load.nextCheckCallDateUtc.toString().includes('Z')
            ? load.nextCheckCallDateUtc.toString()
            : load.nextCheckCallDateUtc.toString() + 'Z',
        );
      }
      load.stopsRatio = `${load.pickUps} : ${load.dropOffs}`;

      const name = userStore.user.name.trim();
      const myOwnedOrder =
        load.assignedTo === name ||
        load.coveredBy === name ||
        load.owner === name ||
        load.capacityOpsRep === name;
      load.my = myOwnedOrder ? 'Yes' : 'No';
      const myCustomers =
        load.accountManager === name || load.salesManager === name;
      load.myCustomers = myCustomers ? 'Yes' : 'No';

      const customerGroups = load.customerGroups?.split(',') ?? [];

      if (
        !Boolean(userStore.user.roles?.includes('Admin')) &&
        !loadBoardStore.groupIds.some((v) => {
          return customerGroups.includes(v.toString());
        }) &&
        userStore.user.id !== load.salesManagerId &&
        userStore.user.id !== load.accountManagerId
      ) {
        load.customerName = '';
        load.customerId = 0;
      }

      const loadNeedsReview = load.orderFlags.some(
        (flag) => flag.flagType === FlagType.NeedsReview,
      );
      const needsReviewEnabled = isFeatureFlagEnabled(
        FeatureFlags.LoadboardEmptyLoads,
      )();
      const addEmptyFlaggedLoadsToGrid = needsReviewEnabled && loadNeedsReview;
      const onlyEmpty = loadBoardStore.onlyEmpty;
      const emptyEnabled = onlyEmpty || addEmptyFlaggedLoadsToGrid;

      //Delete Event
      if (
        msgObj.eventName === WSEventTypeEnum.Deleted &&
        gridApi()?.getRowNode(load.loadId.toString()) !== undefined
      ) {
        gridApi()?.applyTransactionAsync({
          remove: [{ loadId: load.loadId }],
        });
        removeLoadboardItem(load.loadId);
      }

      //Update Event
      if (msgObj.eventName === WSEventTypeEnum.Updated) {
        //Check if load is already in the grid.
        if (gridApi()?.getRowNode(load.loadId.toString()) !== undefined) {
          //Remove empty load if neither the flag or only empty is enabled.
          if (!emptyEnabled && load.status === 'Empty') {
            gridApi()?.applyTransactionAsync({ remove: [load] });
            removeLoadboardItem(load.loadId);
          }
          //Remove non empty load if only empty is enabled.
          else if (onlyEmpty && load.status !== 'Empty') {
            gridApi()?.applyTransactionAsync({ remove: [load] });
            removeLoadboardItem(load.loadId);
          }
          //Update load.
          else if (load.status !== 'Empty' || emptyEnabled) {
            gridApi()?.applyTransactionAsync({ update: [load] });
            updateLoadboardItem(load);
          }
        }
        //Load is not in the grid, so we need to add it. Add any non empty loads or empty loads iff enabled.
        else if (
          (!onlyEmpty && load.status !== 'Empty') ||
          (emptyEnabled && load.status === 'Empty')
        ) {
          gridApi()?.applyTransaction({ add: [load] });
          addLoadboardItem(load);
        }
      }

      props.updatePicksAndDrops?.();
    }
  };

  const columnDefs: Array<ColDef> = props.columnDefs;
  const shouldBeSinglePage = props.singlePage !== undefined && props.singlePage;
  const gridOptions: GridOptions = {
    defaultColDef: {
      editable: false,
      enableRowGroup: false,
      enablePivot: false,
      enableValue: true,
      sortable: true,
      resizable: true,
      filter: true,
      cellDataType: false,
    },
    components: {
      underlinedCellRenderer: memoize(underlinedCellRenderer),
      agDateInput: RelativeDateInput,
    },
    rowBuffer: 30,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return
    getRowId: (params) => params.data.loadId,
    asyncTransactionWaitMillis: 500,
    includeHiddenColumnsInQuickFilter: true,
    columnDefs: columnDefs,
    suppressPaginationPanel: !shouldBeSinglePage,
    pagination: !shouldBeSinglePage,
    paginationPageSize: shouldBeSinglePage ? undefined : 10,
    suppressScrollOnNewData: true,
    rowGroupPanelShow: 'never',
    quickFilterParser: (quickFilter) => quickFilter.split(','),
    cacheQuickFilter: true,
    autoGroupColumnDef: {
      minWidth: 200,
    },
    dataTypeDefinitions: {
      currency: {
        extendsDataType: 'number',
        baseDataType: 'number',
        // @ts-expect-error The type is fine
        valueFormatter: currencyCellFormatter,
        columnTypes: ['currencyType', 'numericColumn'],
      },
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getRowHeight(params) {
      const newHeight = autoHeightColumns.reduce<number | undefined>(
        (newHeight, colId) => {
          const data = params.data as ILoadBoardViewModel | null;
          const column = params.api.getColumn(colId);

          if (column !== null && column.isVisible()) {
            const height = calculateNewHeight(
              // @ts-expect-error types
              data?.[colId as unknown as keyof ILoadBoardViewModel],
              column.getActualWidth(),
            );

            if (
              newHeight === undefined ||
              (height !== undefined && height > newHeight)
            ) {
              return height;
            }
          }

          return newHeight;
        },
        undefined,
      );

      return newHeight;
    },
    suppressDragLeaveHidesColumns: true,
    sideBar: {
      toolPanels: [columnsToolPanel, filtersToolPanel],
    },
    onPaginationChanged: (event) => {
      setPageLimit(event.api.paginationGetPageSize());
      setPageNumber(event.api.paginationGetCurrentPage() + 1);
      setTotal(event.api.paginationGetTotalPages());

      const rowCount = event.api.getDisplayedRowCount();
      const lastGridIndex = rowCount - 1;
      const currentPage = pageNumber() - 1;
      const startPageIndex = currentPage * pagelimit();
      let endPageIndex = (currentPage + 1) * pagelimit() - 1;

      if (endPageIndex > lastGridIndex) {
        endPageIndex = lastGridIndex;
      }

      setShowingCount({
        start: startPageIndex + 1,
        end: endPageIndex + 1,
      });
    },
    onGridPreDestroyed: () => {
      setGridApi(undefined);
    },
    onGridReady(event) {
      setGridApi(event.api);
      const toolPanelInstance = event.api.getToolPanelInstance('filters');
      if (toolPanelInstance !== undefined) {
        toolPanelInstance.expandFilters();
      }

      event.api.addEventListener('filterChanged', () => {
        setFilterModel(event.api.getFilterModel());
      });

      props.onGridReady?.(event.api);
    },
    onCellClicked: (event) => {
      props.onRowClicked?.(event, event.colDef.field);
    },
    ...props.extraOptions,
  };

  createEffect(async function () {
    const socketv = globalState.socketv;

    if (socketv !== undefined) {
      await socketv.invoke('Subscribe', WSEventTypeEnum.Updated);
      await socketv.invoke('Subscribe', WSEventTypeEnum.Deleted);
      await socketv.invoke('Subscribe', WSEventTypeEnum.Created);
      if (socketHandlerInitialized() === false) {
        globalState.socketv!.on('RecieveWebSocketMessage', socketHandler);
      }

      setSocketHandlerInitialized(true);
    }
  });

  createEffect(() => {
    printLog(filterModel());
    if (ConfigManager.showLayout) {
      const filterData = sidebarDataStore.approvals.data.filter(
        (item) => item.pastDue && item.needsAttention && !item.isDisputed,
      );
      setOverdueApprovalsData(filterData);
    }
  });

  const isEnabled: Accessor<boolean> = createMemo(() => {
    const snoozed = sessionStorage.getItem('snoozedApprovals');
    if (snoozed !== null) {
      const currentTimestamp = new Date().getTime();
      const elapsed = currentTimestamp - parseInt(snoozed, 10);
      return elapsed < 48 * 60 * 60 * 1000; // less than 48 hours
    }
    return overdueApprovalsData().length === 0;
  });

  const renderGrid = () => (
    <>
      <Show
        when={
          (props.oldDrops.length > 0 || props.oldPicks.length > 0) &&
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          !props.showOptimized
        }
      >
        <TransitWarning
          oldPicks={props.oldPicks}
          oldDrops={props.oldDrops}
          getRowStyle={getRowStyle}
          onUpdateItems={props.onUpdateItems}
        />
      </Show>
      <Filters
        gridReady={props.gridReady}
        gridOptions={gridOptions}
        columnDefs={columnDefs}
        gridApi={gridApi()}
        onRefreshGrid={props.onRefreshGrid}
        loading={props.loading}
        customFilterCache={props.customFilterCache}
        setCustomFilterCache={props.setCustomFilterCache}
        onUpdatePostings={props.onUpdatePostings}
        showOptimized={props.showOptimized}
        setRestrictToMyUserId={props.setRestrictToMyUserId}
      />
      <Box class="ag-theme-alpine flex flex-col flex-1! min-h-0! small:min-h-[200px]! ">
        <AgGridSolid
          ref={gridRef}
          class="h-full!"
          columnDefs={columnDefs}
          rowData={props.data}
          gridOptions={gridOptions}
          getRowStyle={getRowStyle}
          overlayLoadingTemplate={imageTag}
          noRowsOverlayComponent={imageTag}
          getContextMenuItems={(e) => {
            if (!e.node) {
              return [];
            }

            const node = e.node.data as ILoadBoardViewModel;
            const options = [
              'cut',
              'copy',
              'copyWithHeaders',
              'copyWithGroups',
              'paste',
              {
                name: 'Open in new tab',
                action: () => {
                  const isNative = routeOrderDetailsNewTab(node.orderId);
                  if (isNative) {
                    window.open(`/order/details/${node.orderId}`, '_blank');
                  }
                },
              },
              {
                name: 'Refresh',
                disabled: props.isRefreshingLoadboardRow?.() ?? false,
                action: () => {
                  void props.onRefreshLoadboardRow?.(node.orderId);
                },
              },
              // The "Find Capacity" option will be inserted here.
              'separator',
              'export',
            ];

            if (isFeatureFlagEnabled(FeatureFlags.V4CapacityFinder)()) {
              options.splice(6, 0, {
                name: 'Find Capacity',
                action: () => {
                  window.open(
                    `/capacity/finder?loadNum=${node.loadNumber}`,
                    '_blank',
                  );
                },
              });
            }

            return options;
          }}
        />
      </Box>
      <Show when={!shouldBeSinglePage}>
        <Pagination
          gridApi={gridApi()}
          gridOptions={gridOptions}
          pageLimit={pagelimit()}
          pageNumber={pageNumber()}
          total={total()}
          showingCount={showingCount()}
          length={props.data.length}
        />
      </Show>
    </>
  );

  return (
    <Stack class="h-full! min-h-0! p-2 flex flex-col">
      <Show when={!sidebarDataStore.approvals.loading} fallback={<AtgLoader />}>
        {/* // TODO remove ConfigManager.showLayout when moved to shel page */}
        <Show when={ConfigManager.showLayout} fallback={renderGrid()}>
          <Show
            when={isEnabled()}
            fallback={
              <OverdueApprovalsWarning
                overdueApprovalsData={overdueApprovalsData}
              />
            }
          >
            {renderGrid()}
          </Show>
        </Show>
      </Show>
    </Stack>
  );
};
